import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Search, UserPlus, User, Users, X, Trash2, AlertCircle } from 'lucide-react';
import { Contact, GuestParticipant } from '../../types';
import { anker } from '../../tutorial/anker';
import {
    ParticipantRow,
    addGuest,
    countSelected,
    filterRows,
    normalizeName,
    removeGuest,
    removeGuests,
    setPresenceForIds,
    toContactRows,
    toGuestRows,
    toggleContactPresence,
} from '../../services/participantSelection';

interface Props {
    contacts: Contact[];
    guests: GuestParticipant[];
    /**
     * Setzt Kontakte UND Gaeste in EINEM Aufruf. Zwei getrennte Callbacks waren
     * hier ein Datenverlust: "Keine" raeumt beides ab, beide Handler bauen ihr
     * neues `report` aus derselben Closure, und der zweite Aufruf schrieb den
     * ersten wieder weg — die Anwesenheiten blieben stehen.
     */
    onChange: (contacts: Contact[], guests: GuestParticipant[]) => void;
    onClose: () => void;
    isReadOnly?: boolean;
    /** Aus, wo die aufrufende Ansicht die Gaeste nicht speichern kann. */
    allowGuests?: boolean;
    /**
     * Die Auswahl wurde beim Anlegen der Begehung von selbst geoeffnet, nicht
     * angetippt. Dann bekommt sie eine Anrede und den Knopf "Alleine" — der
     * Pruefer soll die Frage beantworten und nicht bloss wegklicken koennen,
     * ohne dass jemand sieht, ob wirklich niemand dabei war.
     *
     * Der Knopf erscheint nur, solange nichts ausgewaehlt ist: er raeumt damit
     * nie eine getroffene Auswahl ab.
     */
    onSolo?: () => void;
}

/**
 * Teilnehmerauswahl einer Begehung — auf dem Handy als Bottom-Sheet, ab sm als
 * mittiger Dialog. Duenne Huelle: jede Entscheidung steckt in
 * services/participantSelection.ts, hier stehen nur Zustand und Darstellung.
 *
 * Warum eine eigene Flaeche: die Anwesenheit hing bis dahin an einem kleinen
 * Kreis in der Kontakttabelle, die auf dem Handy quer gescrollt werden muss.
 * Auf der Baustelle, im Stehen, ist das der Griff daneben.
 */
export const ParticipantSheet: React.FC<Props> = ({ contacts, guests, onChange, onClose, isReadOnly = false, allowGuests = true, onSolo }) => {
    const [query, setQuery] = useState('');
    const [guestFormOpen, setGuestFormOpen] = useState(false);
    const [guestDraft, setGuestDraft] = useState({ name: '', company: '' });
    const [error, setError] = useState<string | null>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const rows: ParticipantRow[] = useMemo(
        () => [...toGuestRows(guests), ...toContactRows(contacts)],
        [contacts, guests],
    );
    const visible = useMemo(() => filterRows(rows, query), [rows, query]);
    const visibleContactIds = useMemo(() => visible.filter(r => r.kind === 'contact').map(r => r.id), [visible]);
    const visibleGuestIds = useMemo(() => visible.filter(r => r.kind === 'guest').map(r => r.id), [visible]);
    const selected = useMemo(() => rows.filter(r => r.selected), [rows]);
    const total = countSelected(contacts, guests);

    const handleToggle = (row: ParticipantRow) => {
        if (isReadOnly) return;
        setError(null);
        // Einen Gast abzuwaehlen heisst, ihn zu entfernen: er existiert nur,
        // weil er da war.
        if (row.kind === 'guest') onChange(contacts, removeGuest(guests, row.id));
        else onChange(toggleContactPresence(contacts, row.id), guests);
    };

    const handleBulk = (present: boolean) => {
        if (isReadOnly) return;
        setError(null);
        // "Keine" muss auch die sichtbaren Gaeste abraeumen: sie sind immer
        // anwesend, blieben sonst im Zaehler stehen und der Knopf haette
        // gelogen. "Alle" laesst sie unberuehrt — sie sind schon drin.
        onChange(
            setPresenceForIds(contacts, visibleContactIds, present),
            present ? guests : removeGuests(guests, visibleGuestIds),
        );
    };

    const openGuestForm = () => {
        if (isReadOnly || !allowGuests) return;
        // Der Suchtext ist fast immer schon der Name, nach dem vergeblich
        // gesucht wurde — er wird uebernommen, statt ihn abzutippen.
        setGuestDraft({ name: normalizeName(query), company: '' });
        setError(null);
        setGuestFormOpen(true);
    };

    const submitGuest = () => {
        if (isReadOnly) return;
        const res = addGuest(guests, contacts, guestDraft, crypto.randomUUID());
        if (!res.ok) {
            setError(res.message);
            // Steht die Person schon im Verteiler, wird direkt ihr Haken
            // gesetzt — sonst legt der Nutzer sie doch als Gast doppelt an.
            if (res.reason === 'duplicate-contact' && res.existingId) {
                onChange(setPresenceForIds(contacts, [res.existingId], true), guests);
            }
            return;
        }
        onChange(contacts, res.guests);
        setGuestDraft({ name: '', company: '' });
        setGuestFormOpen(false);
        setQuery('');
        setError(null);
        searchRef.current?.focus();
    };

    /*
     * Portal an den <body>: die Flaeche haengt sonst im Baum des Pruefmoduls,
     * dessen Kopfzeile und Reiterleiste den oberen Rand verdeckt haben und
     * dessen Inhaltsspalte (overflow-hidden) den unteren abgeschnitten hat.
     * Ein fixed-Element ist gegen genau das nur ausserhalb sicher.
     *
     * dvh statt vh: mobile Browser zaehlen die Adressleiste in 100vh mit, die
     * unteren 90vh lagen damit teils unter der Leiste — der Knopf "Fertig" war
     * nicht erreichbar.
     */
    return createPortal((
        <div
            className="fixed inset-0 z-[300] bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center animate-fade-in"
            style={{ height: '100dvh' }}
            onClick={onClose}
        >
            <div
                className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col h-[90dvh] sm:h-auto sm:max-h-[82dvh] animate-in slide-in-from-bottom-10 overflow-hidden"
                style={{ maxHeight: 'calc(100dvh - env(safe-area-inset-top))' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Griff — auf dem Handy das Signal, dass die Flaeche von unten kommt */}
                <div className="sm:hidden pt-2.5 pb-1 flex justify-center shrink-0"><div className="w-10 h-1.5 rounded-full bg-slate-200" /></div>

                <div className="px-5 pt-3 pb-4 border-b border-slate-100 shrink-0">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                            <div className="bg-blue-50 text-blue-600 p-2 rounded-xl border border-blue-100"><Users className="w-5 h-5" /></div>
                            <div>
                                <h3 className="font-bold text-slate-800 leading-tight">Teilnehmer der Begehung</h3>
                                <p className="text-xs text-slate-400 font-medium">
                                    {total > 0 ? `${total} ausgewählt` : (onSolo ? 'Wer war bei der Begehung dabei?' : 'Noch niemand ausgewählt')}
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 -mr-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors" aria-label="Schließen"><X className="w-5 h-5" /></button>
                    </div>

                    {selected.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {selected.map(r => (
                                <button
                                    key={`${r.kind}-${r.id}`}
                                    onClick={() => handleToggle(r)}
                                    disabled={isReadOnly}
                                    className={`flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${r.kind === 'guest' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'} ${isReadOnly ? 'pointer-events-none opacity-70' : 'hover:brightness-95'}`}
                                >
                                    <span className="truncate max-w-[11rem]">{r.name || r.company || 'Ohne Namen'}</span>
                                    {!isReadOnly && <X className="w-3.5 h-3.5 shrink-0" />}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-b border-slate-100 shrink-0 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            data-tutorial="teilnehmer.suche"
                            ref={searchRef}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Name, Firma oder Rolle suchen..."
                            className="w-full pl-9 pr-9 py-3 bg-slate-50 border border-slate-200 rounded-xl text-base sm:text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        />
                        {query && (
                            <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600" aria-label="Suche leeren"><X className="w-4 h-4" /></button>
                        )}
                    </div>
                    {!isReadOnly && (
                        <div className="flex items-center gap-2">
                            <button onClick={() => handleBulk(true)} className="flex-1 py-2 text-xs font-bold uppercase tracking-wide text-blue-600 bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100 transition-colors">Alle</button>
                            <button data-tutorial="teilnehmer.keine" onClick={() => handleBulk(false)} className="flex-1 py-2 text-xs font-bold uppercase tracking-wide text-slate-500 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">Keine</button>
                            {allowGuests && (
                                <button data-tutorial="teilnehmer.gast" onClick={openGuestForm} className="flex-[1.4] py-2 text-xs font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors flex items-center justify-center gap-1.5"><UserPlus className="w-3.5 h-3.5" /> Gast</button>
                            )}
                        </div>
                    )}
                    {error && (
                        <div className="flex items-start gap-2 text-[11px] font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" /><span>{error}</span>
                        </div>
                    )}
                </div>

                {guestFormOpen && !isReadOnly && allowGuests && (
                    <div className="px-5 py-3 bg-amber-50/60 border-b border-amber-100 shrink-0 space-y-2">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700">Gast — nur für diese Begehung</p>
                        <input
                            data-tutorial="teilnehmer.gast-name"
                            autoFocus
                            value={guestDraft.name}
                            onChange={e => setGuestDraft(d => ({ ...d, name: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') submitGuest(); }}
                            placeholder="Name"
                            className="w-full px-3 py-2.5 bg-white border border-amber-200 rounded-lg text-base sm:text-sm outline-none focus:ring-2 focus:ring-amber-500/20"
                        />
                        <input
                            value={guestDraft.company}
                            onChange={e => setGuestDraft(d => ({ ...d, company: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') submitGuest(); }}
                            placeholder="Firma (optional)"
                            className="w-full px-3 py-2.5 bg-white border border-amber-200 rounded-lg text-base sm:text-sm outline-none focus:ring-2 focus:ring-amber-500/20"
                        />
                        <div className="flex gap-2 pt-0.5">
                            <button onClick={() => { setGuestFormOpen(false); setError(null); }} className="flex-1 py-2.5 text-sm font-bold text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">Abbrechen</button>
                            <button onClick={submitGuest} className="flex-1 py-2.5 text-sm font-bold text-white bg-amber-600 rounded-lg hover:bg-amber-700 active:scale-[0.98] transition-all">Hinzufügen</button>
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto overscroll-contain">
                    {visible.length === 0 ? (
                        <div className="p-8 text-center">
                            <p className="text-sm text-slate-400 font-medium">Niemand gefunden.</p>
                            {!isReadOnly && allowGuests && query.trim() && (
                                <button onClick={openGuestForm} className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 bg-amber-600 text-white text-sm font-bold rounded-xl hover:bg-amber-700 active:scale-[0.98] transition-all">
                                    <UserPlus className="w-4 h-4" /> {normalizeName(query)} als Gast
                                </button>
                            )}
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-50">
                            {visible.map((r, teilnehmerIndex) => (
                                <li key={`${r.kind}-${r.id}`}>
                                    <button
                                        {...anker('teilnehmer.zeile', teilnehmerIndex === 0)}
                                        onClick={() => handleToggle(r)}
                                        disabled={isReadOnly}
                                        className={`w-full flex items-center gap-3 px-5 py-3.5 min-h-[60px] text-left transition-colors ${r.selected ? 'bg-blue-50/40' : 'bg-white'} ${isReadOnly ? 'pointer-events-none opacity-70' : 'hover:bg-slate-50 active:bg-slate-100'}`}
                                    >
                                        <span className={`w-7 h-7 shrink-0 rounded-lg border-2 flex items-center justify-center transition-all ${r.selected ? (r.kind === 'guest' ? 'bg-amber-500 border-amber-500' : 'bg-blue-600 border-blue-600') : 'border-slate-200 bg-white'}`}>
                                            {r.selected && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="flex items-center gap-2">
                                                <span className={`font-bold text-sm truncate ${r.selected ? 'text-slate-900' : 'text-slate-700'}`}>{r.name || 'Ohne Namen'}</span>
                                                {r.kind === 'guest' && <span className="shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">Gast</span>}
                                            </span>
                                            <span className="block text-xs text-slate-400 truncate">{[r.company, r.role].filter(Boolean).join(' · ') || '—'}</span>
                                        </span>
                                        {r.kind === 'guest' && !isReadOnly && <Trash2 className="w-4 h-4 shrink-0 text-slate-300" />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-slate-100 bg-white shrink-0 flex gap-2" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
                    {onSolo && total === 0 && !isReadOnly && (
                        <button onClick={onSolo} className="flex-1 py-3.5 bg-white text-slate-700 font-bold rounded-xl border border-slate-200 hover:bg-slate-50 active:scale-[0.99] transition-all flex items-center justify-center gap-2">
                            <User className="w-4 h-4" /> Alleine
                        </button>
                    )}
                    <button data-tutorial="teilnehmer.fertig" onClick={onClose} className="flex-1 py-3.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 active:scale-[0.99] transition-all">
                        Fertig{total > 0 ? ` — ${total} Teilnehmer` : ''}
                    </button>
                </div>
            </div>
        </div>
    ), document.body);
};
